import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { filterEmptyValues } from 'utils/helpers/filterEmptyValues'
import useQuery from 'hooks/useQuery'
import { Button, Dropdown, DurationInput, TextInput } from 'components/common'
import { INVENTORY_FORM_FIELDS } from 'utils/constants'
import {
  getDepartmentOptions,
  getFamiliesOptionsBySection,
  getSectionOptionsByDepartment,
} from 'utils/helpers'
import { DateTime } from 'components/common/input/dateTime'
const {
  ITEM_NUMBER,
  BARCODE,
  DEPARTMENT,
  ERP_BRAND,
  ERP_ITEM_DESC,
  ERP_PACK_INFO,
  ERP_UOM,
  FAMILY,
  ONLINE_DESC,
  ONLINE_PACK_INFO,
  QTY_PER_UOM,
  SECTION,
  SUB_FAMILY,
  SAP_DEPARTMENT,
  SAP_SECTION,
  SAP_FAMILY,
  SAP_SUB_FAMILY,
  ERP_QTY,
  LAST_QTY_UPDATE,
  OOS_THRESHOLD,
  STORE_ID,
  // STORE_NAME,
  ON_STOCK,
} = INVENTORY_FORM_FIELDS

type Props = {
  toggleFilterForm: React.Dispatch<React.SetStateAction<boolean>>
}

export const FILTER_FIELD_INVENTORY = 'inventoryFilters'

const InventoryFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_INVENTORY)
  const [resetFilter, setResetFilter] = useState(false)

  const { register, handleSubmit, setValue, reset, watch } = useForm({
    defaultValues: filters,
  })

  const onFilterApply = (data: any) => {
    if (data?.dept === 'NONE') {
      delete data.dept
    }

    if (data?.section === 'NONE') {
      delete data.section
    }

    if (data?.family === 'NONE') {
      delete data.family
    }

    if (data?.sapDept === 'NONE') {
      delete data.sapDept
    }

    if (data?.sapSection === 'NONE') {
      delete data.sapSection
    }

    if (data?.sapFamily === 'NONE') {
      delete data.sapFamily
    }

    if (data?.isOnStock === 'NONE') {
      delete data.isOnStock
    }
    const filteredData = filterEmptyValues(data)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({ inventoryFilters: filteredData, page: 1 })
    } else {
      removeQuery(FILTER_FIELD_INVENTORY)
    }

    toggleFilterForm((pre) => !pre)
  }

  // will watch the change of Department and Section Fields
  const deptWatch = watch('dept')
  const sectionWatch = watch('section')

  // Department Options
  const deptOptions = useMemo(() => getDepartmentOptions(), [])

  //  Section Options
  const sectionOptions = useMemo(() => {
    const dept = deptWatch || deptOptions[0]?.value

    return getSectionOptionsByDepartment(dept)
  }, [deptWatch, getSectionOptionsByDepartment])

  //  Family Options
  const familyOptions = useMemo(() => {
    const section = sectionWatch || sectionOptions[0]?.value

    return getFamiliesOptionsBySection(section)
  }, [sectionWatch, deptWatch, sectionOptions])

  // The SAP hierarchy cascades off the SAP lookup tables, so it needs its own
  // watches and option lists rather than sharing the legacy ones.
  const sapDeptWatch = watch(SAP_DEPARTMENT.key)
  const sapSectionWatch = watch(SAP_SECTION.key)

  const sapDeptOptions = useMemo(() => getDepartmentOptions(true), [])

  const sapSectionOptions = useMemo(() => {
    const dept = sapDeptWatch || sapDeptOptions[0]?.value

    return getSectionOptionsByDepartment(dept, true)
  }, [sapDeptWatch, sapDeptOptions])

  const sapFamilyOptions = useMemo(() => {
    const section = sapSectionWatch || sapSectionOptions[0]?.value

    return getFamiliesOptionsBySection(section, true)
  }, [sapSectionWatch, sapDeptWatch, sapSectionOptions])

  const onStockOptions = [
    { label: '--- NONE ---', value: 'NONE', disabled: false },
    { value: 'true', label: 'Yes', disabled: false },
    { value: 'false', label: 'No', disabled: false },
  ]

  const cancelFilter = () => {
    reset(filters)
    setResetFilter((pre) => !pre)
    toggleFilterForm((pre) => !pre)
  }

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3 ">
      <div className=" mx-auto max-w-[900px]">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="flex flex-wrap gap-x-14 gap-y-3">
            {/* <div className="w-64">
              <TextInput
                register={register}
                name={STORE_NAME.key}
                label={STORE_NAME.label}
              />
            </div> */}
            <div className="w-64 text-gray-600">
              <TextInput
                register={register}
                name={STORE_ID.key}
                label={STORE_ID.label}
              />
            </div>
            <div className="w-64 text-gray-600">
              <DurationInput
                register={register}
                startText="From"
                endText="To"
                title={ITEM_NUMBER.label}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={BARCODE.key}
                label={BARCODE.label}
              />
            </div>
            <div className="w-64">
              {/* Department */}
              <Dropdown
                name={DEPARTMENT.key}
                label={DEPARTMENT.label}
                options={deptOptions}
                setValue={setValue}
                defaultValue={filters?.dept || resetFilter}
              />
            </div>
            <div className="w-64">
              {/* Section */}
              <Dropdown
                name={SECTION.key}
                label={SECTION.label}
                options={sectionOptions}
                setValue={setValue}
                defaultValue={filters?.section || sectionOptions?.[1]}
              />
            </div>
            <div className="w-64">
              <Dropdown
                name={FAMILY.key}
                label={FAMILY.label}
                options={familyOptions}
                setValue={setValue}
                defaultValue={filters?.family || familyOptions?.[1]}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={SUB_FAMILY.key}
                label={SUB_FAMILY.label}
              />
            </div>
            <div className="w-64">
              {/* SAP Department */}
              <Dropdown
                name={SAP_DEPARTMENT.key}
                label={SAP_DEPARTMENT.label}
                options={sapDeptOptions}
                setValue={setValue}
                defaultValue={filters?.sapDept || resetFilter}
              />
            </div>
            <div className="w-64">
              {/* SAP Section */}
              <Dropdown
                name={SAP_SECTION.key}
                label={SAP_SECTION.label}
                options={sapSectionOptions}
                setValue={setValue}
                defaultValue={filters?.sapSection || sapSectionOptions?.[1]}
              />
            </div>
            <div className="w-64">
              {/* SAP Family */}
              <Dropdown
                name={SAP_FAMILY.key}
                label={SAP_FAMILY.label}
                options={sapFamilyOptions}
                setValue={setValue}
                defaultValue={filters?.sapFamily || sapFamilyOptions?.[1]}
              />
            </div>
            <div className="w-64">
              {/* SAP Sub Family */}
              <TextInput
                register={register}
                name={SAP_SUB_FAMILY.key}
                label={SAP_SUB_FAMILY.label}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={ERP_ITEM_DESC.key}
                label={ERP_ITEM_DESC.label}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={ERP_UOM.key}
                label={ERP_UOM.label}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={QTY_PER_UOM.key}
                label={QTY_PER_UOM.label}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={ERP_PACK_INFO.key}
                label={ERP_PACK_INFO.label}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={ERP_BRAND.key}
                label={ERP_BRAND.label}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={ONLINE_DESC.key}
                label={ONLINE_DESC.label}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={ONLINE_PACK_INFO.key}
                label={ONLINE_PACK_INFO.label}
              />
            </div>
            <div className="w-64">
              <DateTime
                label={LAST_QTY_UPDATE.label}
                name={LAST_QTY_UPDATE.key}
                register={register}
                required={true}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={ERP_QTY.key}
                label={ERP_QTY.label}
              />
            </div>
            <div className="w-64">
              <TextInput
                register={register}
                name={OOS_THRESHOLD.key}
                label={OOS_THRESHOLD.label}
              />
            </div>

            <div className="w-64">
              <Dropdown
                name={ON_STOCK.key}
                label={ON_STOCK.label}
                setValue={setValue}
                defaultValue={filters?.[ON_STOCK.key] || resetFilter}
                options={onStockOptions}
              />
            </div>
          </div>
          <div className="my-4 flex gap-5">
            <Button buttonText="Apply Filters" className="w-32" />
            <Button
              buttonText="cancel"
              variant="outline"
              onClick={cancelFilter}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default InventoryFilterForm
